import {
  companyFileListResponseSchema,
  companyFileSchema,
  companyInternalNoteListResponseSchema,
  companyInternalNoteSchema,
  companyInvitationSchema,
  companyMemberListResponseSchema,
  companyMemberSchema,
  companyModerationListResponseSchema,
  companyOnboardingResponseSchema,
  type CompanyFile,
  type CompanyFileCreateRequest,
  type CompanyFileKind,
  type CompanyFileListResponse,
  type CompanyInternalNote,
  type CompanyInternalNoteCreateRequest,
  type CompanyInternalNoteListResponse,
  type CompanyInvitation,
  type CompanyInvitationCreateRequest,
  type CompanyMember,
  type CompanyMemberListResponse,
  type CompanyMemberUpdateRequest,
  type CompanyModerationDecisionRequest,
  type CompanyModerationListResponse,
  type CompanyOnboardingCreate,
  type CompanyOnboardingResponse,
  type CompanyOnboardingUpdate,
  type CompanyStatus,
  type CompanyType,
} from "@brainics/api-contract";

import { requestJson } from "./api.js";

function jsonInit(method: string, payload: unknown): RequestInit {
  return {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  };
}

function queryString(params: Record<string, string | number | boolean | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `?${text}` : "";
}

export async function createCompany(
  input: CompanyOnboardingCreate,
): Promise<CompanyOnboardingResponse> {
  return requestJson("/api/v1/companies", companyOnboardingResponseSchema, jsonInit("POST", input));
}

export async function getCompanyOnboarding(companyId: string): Promise<CompanyOnboardingResponse> {
  return requestJson(`/api/v1/companies/${companyId}/onboarding`, companyOnboardingResponseSchema);
}

export async function updateCompanyOnboarding(
  companyId: string,
  input: CompanyOnboardingUpdate,
): Promise<CompanyOnboardingResponse> {
  return requestJson(
    `/api/v1/companies/${companyId}/onboarding`,
    companyOnboardingResponseSchema,
    jsonInit("PATCH", input),
  );
}

export async function submitCompanyModeration(
  companyId: string,
): Promise<CompanyOnboardingResponse> {
  return requestJson(
    `/api/v1/companies/${companyId}/moderation/submit`,
    companyOnboardingResponseSchema,
    jsonInit("POST", {}),
  );
}

export async function listCompanyMembers(companyId: string): Promise<CompanyMemberListResponse> {
  return requestJson(`/api/v1/companies/${companyId}/members`, companyMemberListResponseSchema);
}

export async function updateCompanyMember(
  companyId: string,
  membershipId: string,
  input: CompanyMemberUpdateRequest,
): Promise<CompanyMember> {
  return requestJson(
    `/api/v1/companies/${companyId}/members/${membershipId}`,
    companyMemberSchema,
    jsonInit("PATCH", input),
  );
}

export async function createCompanyInvitation(
  companyId: string,
  input: CompanyInvitationCreateRequest,
): Promise<CompanyInvitation> {
  return requestJson(
    `/api/v1/companies/${companyId}/invitations`,
    companyInvitationSchema,
    jsonInit("POST", input),
  );
}

export async function listCompanyFiles(
  companyId: string,
  kind?: CompanyFileKind,
): Promise<CompanyFileListResponse> {
  return requestJson(
    `/api/v1/companies/${companyId}/files${queryString({ kind })}`,
    companyFileListResponseSchema,
  );
}

export async function createCompanyFile(
  companyId: string,
  input: CompanyFileCreateRequest,
): Promise<CompanyFile> {
  return requestJson(
    `/api/v1/companies/${companyId}/files`,
    companyFileSchema,
    jsonInit("POST", input),
  );
}

export async function archiveCompanyFile(companyId: string, fileId: string): Promise<CompanyFile> {
  return requestJson(`/api/v1/companies/${companyId}/files/${fileId}`, companyFileSchema, {
    method: "DELETE",
  });
}

export async function listCompanyInternalNotes(
  companyId: string,
): Promise<CompanyInternalNoteListResponse> {
  return requestJson(
    `/api/v1/companies/${companyId}/internal-notes`,
    companyInternalNoteListResponseSchema,
  );
}

export async function createCompanyInternalNote(
  companyId: string,
  input: CompanyInternalNoteCreateRequest,
): Promise<CompanyInternalNote> {
  return requestJson(
    `/api/v1/companies/${companyId}/internal-notes`,
    companyInternalNoteSchema,
    jsonInit("POST", input),
  );
}

export async function listCompanyModerationQueue(input: {
  status?: CompanyStatus;
  type?: CompanyType;
  limit?: number;
  offset?: number;
}): Promise<CompanyModerationListResponse> {
  return requestJson(
    `/api/v1/companies/moderation${queryString(input)}`,
    companyModerationListResponseSchema,
  );
}

export async function decideCompanyModeration(
  companyId: string,
  input: CompanyModerationDecisionRequest,
): Promise<CompanyOnboardingResponse> {
  return requestJson(
    `/api/v1/companies/moderation/${companyId}/decision`,
    companyOnboardingResponseSchema,
    jsonInit("POST", input),
  );
}
